import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  isAdmin: boolean
  signIn: (email: string, password: string, captchaToken?: string) => Promise<void>
  signInWithPasskey: (captchaToken?: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Capture native fetch reference at module load time (before console manipulation)
const nativeFetch = window.fetch.bind(window)

const ADMIN_CACHE_KEY = 'crm_admin_v2'

interface AuthProviderProps {
  children: ReactNode
}

function getCachedAdminStatus(userId: string): boolean | null {
  try {
    const cached = localStorage.getItem(ADMIN_CACHE_KEY)
    if (!cached) return null
    const parsed = JSON.parse(cached)
    if (parsed.userId === userId && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
      return parsed.isAdmin
    }
  } catch { /* ignore */ }
  return null
}

function setCachedAdminStatus(userId: string, isAdmin: boolean) {
  try {
    localStorage.setItem(ADMIN_CACHE_KEY, JSON.stringify({ userId, isAdmin, timestamp: Date.now() }))
  } catch { /* ignore */ }
}

// Vérifie le statut admin via le client Supabase
async function checkIsAdmin(userId: string, attempt = 1): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('admins')
      .select('id')
      .eq('user_id', userId)
      .single()

    if (!error && !!data) return true

    if (attempt === 1) {
      await new Promise(r => setTimeout(r, 500))
      return checkIsAdmin(userId, 2)
    }
    return false
  } catch {
    if (attempt === 1) {
      await new Promise(r => setTimeout(r, 500))
      return checkIsAdmin(userId, 2)
    }
    return false
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const initializedRef = useRef(false)

  const handleSession = useCallback(async (newSession: Session | null) => {
    setSession(newSession)
    setUser(newSession?.user ?? null)

    if (newSession?.user) {
      const userId = newSession.user.id

      // 1. Cache pour affichage instantané (UX)
      const cached = getCachedAdminStatus(userId)
      if (cached === true) {
        setIsAdmin(true)
        setLoading(false)
        // Re-vérifier en arrière-plan
        checkIsAdmin(userId).then((actual) => {
          setCachedAdminStatus(userId, actual)
          if (!actual) setIsAdmin(false)
        })
        return
      }

      // 2. Pas de cache : vérifier côté serveur
      const admin = await checkIsAdmin(userId)
      setCachedAdminStatus(userId, admin)
      setIsAdmin(admin)
    } else {
      setIsAdmin(false)
      try { localStorage.removeItem(ADMIN_CACHE_KEY) } catch { /* ignore */ }
    }

    setLoading(false)
  }, [])

  useEffect(() => {
    if (initializedRef.current) return
    initializedRef.current = true

    // Timeout de sécurité : si rien ne répond après 5s, on arrête le loading
    const safetyTimeout = setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          console.warn('[Auth] Safety timeout: forcing loading=false after 5s')
          return false
        }
        return prev
      })
    }, 5000)

    // Listen for auth changes (fires INITIAL_SESSION first)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      clearTimeout(safetyTimeout)
      await handleSession(session)
    })

    // Fallback: si onAuthStateChange ne fire pas
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setLoading((prev) => {
        if (prev) {
          handleSession(session)
        }
        return prev
      })
    }).catch(() => {
      clearTimeout(safetyTimeout)
      setLoading(false)
    })

    return () => {
      clearTimeout(safetyTimeout)
      subscription.unsubscribe()
    }
  }, [handleSession])

  // Track login for security alerts (fire and forget, no await)
  const trackLogin = (accessToken: string) => {
    const apiUrl = import.meta.env.VITE_API_URL || ''
    setTimeout(() => {
      nativeFetch(`${apiUrl}/api/security/track-login`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }).catch(() => { /* non-blocking */ })
    }, 2000) // Delay to avoid race with onAuthStateChange
  }

  const signIn = async (email: string, password: string, captchaToken?: string) => {
    const { data: signInData, error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: captchaToken ? { captchaToken } : undefined,
    })
    if (error) throw error

    if (signInData.session) {
      trackLogin(signInData.session.access_token)
    }
  }

  const signInWithPasskey = async (captchaToken?: string) => {
    const { data, error } = await supabase.auth.signInWithPasskey(
      captchaToken ? { options: { captchaToken } } : undefined
    )
    if (error) throw error

    if (data?.session) {
      trackLogin(data.session.access_token)
    }
  }

  const signOut = useCallback(async () => {
    try { localStorage.removeItem(ADMIN_CACHE_KEY) } catch { /* ignore */ }
    try { localStorage.removeItem('crm_admin_status') } catch { /* ignore */ }
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }, [])

  // Session timeout: auto-logout after 30 min of inactivity
  useEffect(() => {
    if (!user) return

    const TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes
    let timeoutId: ReturnType<typeof setTimeout>

    const resetTimer = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        console.warn('[Auth] Session timeout: auto-logout after 30min inactivity')
        signOut()
      }, TIMEOUT_MS)
    }

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }))
    resetTimer()

    return () => {
      clearTimeout(timeoutId)
      events.forEach(e => window.removeEventListener(e, resetTimer))
    }
  }, [user, signOut])

  const value = {
    user,
    session,
    loading,
    isAdmin,
    signIn,
    signInWithPasskey,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
